import { FaceSmileIcon } from "@heroicons/react/24/outline";
import { ArrowUpIcon, HandThumbUpIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "urql";
import { useMyself } from "../_context/UserContext";
import { confirmReactionHintMutation } from "../_lib/graphql/mutations";
import { Button } from "../_ui/Button";

type PropTypes = {
  target: React.RefObject<HTMLElement>
}

// Einmaliger Hinweis auf die Reaktionen-Funktion; "Alles klar" wird im Profil gespeichert
export const ReactionHint = ({ target }: PropTypes) => {
  const { reactionHintConfirmed, IsLoggedIn } = useMyself() || {}
  const [state, executeConfirm] = useMutation(confirmReactionHintMutation)
  const [dismissed, setDismissed] = useState(false)
  const [rect, setRect] = useState<DOMRect | null>(null)

  const visible = !!IsLoggedIn && reactionHintConfirmed === false && !dismissed

  useEffect(() => {
    if (!visible) {
      return
    }
    const update = () => setRect(target.current?.getBoundingClientRect() || null)
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [visible, target])

  if (!visible || !rect) {
    return null
  }

  const handleConfirm = () => {
    setDismissed(true)
    executeConfirm({}).then(res => {
      if (res.error) {
        console.error(res.error)
        setDismissed(false)
      }
    })
  }

  const centerX = rect.left + rect.width / 2
  const pad = 6
  const arrowTop = rect.bottom + pad + 6
  const cardWidth = Math.min(320, window.innerWidth - 32)
  const cardLeft = Math.max(16, Math.min(window.innerWidth - cardWidth - 16, centerX - cardWidth / 2))

  return createPortal(
    <div className={'fixed inset-0 z-[100]'} role="dialog" aria-modal="true" aria-label="Neue Funktion">
      {/* Abdunkelung mit Aussparung um das Emoji-Icon */}
      <div
        className={'absolute rounded-full pointer-events-none'}
        style={{
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
        }}
      >
        <span className={'absolute inset-0 rounded-full ring-2 ring-sky-400 animate-ping'}/>
        <span className={'absolute inset-0 rounded-full ring-2 ring-sky-400'}/>
      </div>

      {/* Animierter Pfeil */}
      <div
        className={'absolute -translate-x-1/2 pointer-events-none'}
        style={{ top: arrowTop, left: centerX }}
      >
        <div className={'animate-bounce text-sky-400'}>
          <ArrowUpIcon className={'block w-9 h-9 drop-shadow-lg'}/>
        </div>
      </div>

      {/* Erklärung */}
      <div
        className={'absolute rounded-xl border border-neutral-700 bg-neutral-900 p-5 shadow-2xl text-white'}
        style={{ top: arrowTop + 48, left: cardLeft, width: cardWidth }}
      >
        <h2 className={'text-lg font-semibold pb-2'}>Neu: Reaktionen 🎉</h2>
        <p className={'text-sm text-neutral-300 pb-3'}>
          Du kannst jetzt in der Ergebnisliste auf die Ergebnisse anderer mit einem Emoji reagieren.
        </p>
        {/* Beispiel, wie Reaktionen in der Ergebnisliste aussehen */}
        <div aria-hidden="true" className={'mb-3 rounded-lg bg-neutral-800 ring-1 ring-neutral-700 px-3 py-2 pointer-events-none select-none'}>
          <div className={'flex items-center justify-between text-sm'}>
            <span className={'font-medium'}>Max Mustermann</span>
            <span className={'text-neutral-300'}>185 lbs</span>
          </div>
          <div className={'flex items-center justify-end gap-1.5 pt-2'}>
            <span className={'flex items-center gap-1 rounded-full px-2.5 min-h-[32px] text-xs ring-1 ring-sky-500 bg-sky-500/10 text-sky-300'}>
              <span className={'text-base leading-none'}>🔥</span>
              <span>3</span>
            </span>
            <span className={'flex items-center gap-1 rounded-full px-2.5 min-h-[32px] text-xs ring-1 ring-neutral-700 text-neutral-300'}>
              <span className={'text-base leading-none'}>💪</span>
              <span>1</span>
            </span>
            <span className={'flex items-center justify-center rounded-full w-8 h-8 ring-1 ring-neutral-700 text-neutral-400'}>
              <FaceSmileIcon className={'w-5 h-5'}/>
            </span>
          </div>
        </div>

        <p className={'text-sm text-neutral-300 pb-4'}>
          Reagiert jemand auf dein Ergebnis, erscheint hier oben ein Zähler. Tippe auf das Emoji,
          um zu sehen, wer wie reagiert hat, und springe direkt zum Ergebnis.
        </p>
        <Button disabled={state.fetching} onClick={handleConfirm} color={'default'} variant={'contained'} fullWidth>
          <HandThumbUpIcon className={'w-5 h-5'}/>
          Alles klar
        </Button>
      </div>
    </div>,
    document.body
  )
}
